import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps
} from '@mui/material';
import { FC, useEffect } from 'react';
import { useQuery } from 'react-query';
import { getFieldWalkthroughLocations } from '../../fetch';
import { IFieldWalkthroughLocation } from '../../models';

interface OnChangeInfo {
  isFirst: boolean;
  isLast: boolean;
}

interface IFieldWalkthroughLocationDropdownProps {
  fieldWalkthroughId: string;
  value: number;
  onChange: (val: number, onChangeInfo?: OnChangeInfo) => void;
  onDataLoad?: (data: IFieldWalkthroughLocation[]) => void;
  isDisabled?: boolean;
  setOptions?: (val: any[]) => void;
  isRequired?: boolean;
  showAll?: boolean;
  isLoadingDropdown?: (val: boolean) => void;
  hasAllLocationsOption?: boolean;
  sx?: SxProps;
}

export const FieldWalkthroughLocationDropdown: FC<IFieldWalkthroughLocationDropdownProps> = ({
  fieldWalkthroughId,
  isDisabled,
  isRequired,
  value,
  onDataLoad,
  onChange,
  setOptions,
  showAll,
  isLoadingDropdown,
  hasAllLocationsOption,
  sx
}) => {
  const { isLoading, data } = useQuery<IFieldWalkthroughLocation[], Error>(
    ['field-walkthrough-locations'],
    async () => await getFieldWalkthroughLocations(fieldWalkthroughId),
    {
      onSuccess: d => {
        if (setOptions) {
          setOptions(d);
        }
        onChange(hasAllLocationsOption ? -1 : d?.[0].fieldWalkthroughLocationID, {
          isFirst: true,
          isLast: false
        });
        onDataLoad?.(d);
        if (!showAll) {
          onChange(d[0].fieldWalkthroughLocationID, { isFirst: true, isLast: false });
        }
      }
    }
  );

  useEffect(() => {
    isLoadingDropdown?.(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    const selectedId = +event.target.value;
    const index = data.findIndex(location => location.fieldWalkthroughLocationID === selectedId);
    const isFirst = index === 0;
    const isLast = index === data.length - 1;
    onChange(Number(selectedId), { isFirst, isLast });
  };

  return (
    <FormControl
      sx={{ minWidth: '15rem', ...sx }}
      variant="outlined"
      size="small"
      required={isRequired}
    >
      <InputLabel shrink={!isLoading} id={`locationId`}>
        {isLoading ? 'Loading...' : 'Location'}
      </InputLabel>
      <Select
        disabled={isDisabled || isLoading}
        name={`locationId`}
        labelId={`locationId`}
        id={`locationId`}
        value={value}
        size="small"
        onChange={handleChange}
      >
        {!isLoading && data?.length === 0 && <MenuItem disabled>No Locations available</MenuItem>}
        {!isLoading && showAll && <MenuItem value={-1}>All</MenuItem>}
        {!isLoading &&
          data?.map((record, index) => {
            return (
              <MenuItem key={`${index}`} value={record.fieldWalkthroughLocationID}>
                {record.fieldWalkthroughLocationName}
              </MenuItem>
            );
          })}
      </Select>
    </FormControl>
  );
};
